import { apiRequest, ApiRequestOptions } from '../../utils/apiService';
import {
  type ApprovalState,
  type BookingProposalSummary,
  type CatalogItemInput,
  type CommercialRules,
  type ConvertLeadInput,
  type EventsApproval,
  type EventsVenueSettings,
  type ResolveBlockedInput,
  type SpaceInput,
  type VenueSettingsInput,
  EventsApiError,
  type EventsBooking,
  type EventsCatalogItem,
  type EventsBookingFilters,
  type EventsBookingRefund,
  type EventsConflict,
  type EventsLead,
  type EventsOrganization,
  type EventsPaymentRequest,
  type EventsPipeline,
  type EventsSpace,
  type LeadUpdate,
  type EventsScope,
  type EventsSession,
  type EventsTimelineEntry,
  type PricingResult,
  type ProposalDraftInput,
  type ProposalLineInput,
  type ProposalPreview,
  type ProposalVersion,
  type PublishResult,
} from './api/types';

const baseURL = process.env.REACT_APP_API_BASE_URL;

const TENANT_HEADERS = new Set(['x-store-id', 'x-store-group-id']);

export type EventsRequestOptions = Omit<
  ApiRequestOptions,
  'storeGroupId' | 'storeId' | 'skipStoreHeader'
> & {
  organizationId: number;
  venueId?: number | null;
};

function eventsUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${baseURL}/api/events${normalizedPath}`;
}

function withoutTenantHeaders(headers: HeadersInit | undefined) {
  if (!headers) return undefined;
  const entries =
    headers instanceof Headers
      ? Array.from(headers.entries())
      : Array.isArray(headers)
        ? headers
        : Object.entries(headers);
  return Object.fromEntries(
    entries.filter(([name]) => !TENANT_HEADERS.has(name.toLowerCase())),
  );
}

/**
 * Scoped events request. Organization-level calls send only
 * X-Store-Group-ID; venue-level calls add an explicit X-Store-ID.
 * localStorage.currentStoreId is never consulted.
 */
export function eventsApiRequest(
  path: string,
  { organizationId, venueId = null, headers, ...options }: EventsRequestOptions,
): Promise<Response | null> {
  return apiRequest(eventsUrl(path), {
    ...options,
    headers: withoutTenantHeaders(headers),
    storeGroupId: organizationId,
    storeId: venueId,
  });
}

/** GET organizations/ is authenticated but takes no tenant headers. */
export function eventsBootstrapRequest(
  path: string,
  {
    headers,
    ...options
  }: Omit<EventsRequestOptions, 'organizationId' | 'venueId'> = {},
): Promise<Response | null> {
  return apiRequest(eventsUrl(path), {
    ...options,
    headers: withoutTenantHeaders(headers),
    storeId: null,
  });
}

/**
 * DRF errors arrive as {detail}, a field map ({field: [messages]}), or a
 * bare list. Returns the first message a person can read.
 */
export function errorDetail(body: unknown): string | null {
  if (typeof body === 'string') return body || null;
  if (Array.isArray(body)) {
    for (const item of body) {
      const message = errorDetail(item);
      if (message) return message;
    }
    return null;
  }
  if (body && typeof body === 'object') {
    const record = body as Record<string, unknown>;
    if ('detail' in record) return errorDetail(record.detail);
    for (const value of Object.values(record)) {
      const message = errorDetail(value);
      if (message) return message;
    }
  }
  return null;
}

async function readJson<T>(
  response: Response | null,
  label: string,
): Promise<T> {
  if (!response) {
    throw new EventsApiError(`${label}: not signed in`, null);
  }
  if (!response.ok) {
    let body: unknown = null;
    try {
      body = await response.json();
    } catch {
      body = null;
    }
    const conflicts =
      body &&
      typeof body === 'object' &&
      Array.isArray((body as { conflicts?: unknown }).conflicts)
        ? (body as { conflicts: EventsConflict[] }).conflicts
        : [];
    throw new EventsApiError(
      `${label} failed (${response.status})`,
      response.status,
      errorDetail(body),
      conflicts,
    );
  }
  return (await response.json()) as T;
}

async function scopedJson<T>(
  scope: EventsScope,
  path: string,
  label: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const response = await eventsApiRequest(path, {
    method: init.method ?? 'GET',
    organizationId: scope.organizationId,
    venueId: scope.venueId,
    ...(init.body !== undefined && { body: JSON.stringify(init.body) }),
  });
  return readJson<T>(response, label);
}

export async function fetchEventsOrganizations(): Promise<
  EventsOrganization[]
> {
  const response = await eventsBootstrapRequest('/organizations/', {
    method: 'GET',
  });
  return readJson<EventsOrganization[]>(response, 'Events organizations');
}

export async function fetchEventsSession(
  scope: EventsScope,
): Promise<EventsSession> {
  const response = await eventsApiRequest('/session/', {
    method: 'GET',
    organizationId: scope.organizationId,
    venueId: scope.venueId,
  });
  return readJson<EventsSession>(response, 'Events session');
}

export async function fetchEventsBookings(
  scope: EventsScope,
  filters: EventsBookingFilters = {},
): Promise<EventsBooking[]> {
  const query = filters.state
    ? `?state=${encodeURIComponent(filters.state)}`
    : '';
  const response = await eventsApiRequest(`/bookings/${query}`, {
    method: 'GET',
    organizationId: scope.organizationId,
    venueId: scope.venueId,
  });
  return readJson<EventsBooking[]>(response, 'Events bookings');
}

export async function fetchBookingPaymentSchedule(
  scope: EventsScope,
  bookingId: number,
): Promise<EventsPaymentRequest[]> {
  const response = await eventsApiRequest(
    `/bookings/${bookingId}/payment-schedule/`,
    {
      method: 'GET',
      organizationId: scope.organizationId,
      venueId: scope.venueId,
    },
  );
  return readJson<EventsPaymentRequest[]>(response, 'Payment schedule');
}

export async function fetchBookingRefunds(
  scope: EventsScope,
  bookingId: number,
): Promise<EventsBookingRefund[]> {
  const response = await eventsApiRequest(`/bookings/${bookingId}/refunds/`, {
    method: 'GET',
    organizationId: scope.organizationId,
    venueId: scope.venueId,
  });
  return readJson<EventsBookingRefund[]>(response, 'Booking refunds');
}

export function fetchLeads(scope: EventsScope): Promise<EventsLead[]> {
  return scopedJson(scope, '/leads/', 'Inquiries');
}

export function updateLead(
  scope: EventsScope,
  leadId: number,
  values: LeadUpdate,
): Promise<EventsLead> {
  return scopedJson(scope, `/leads/${leadId}/`, 'Update inquiry', {
    method: 'PATCH',
    body: values,
  });
}

export function qualifyLead(
  scope: EventsScope,
  leadId: number,
): Promise<EventsLead> {
  return scopedJson(scope, `/leads/${leadId}/qualify/`, 'Qualify inquiry', {
    method: 'POST',
    body: {},
  });
}

export function loseLead(
  scope: EventsScope,
  leadId: number,
  reason: string,
): Promise<EventsLead> {
  return scopedJson(scope, `/leads/${leadId}/lose/`, 'Mark inquiry lost', {
    method: 'POST',
    body: { reason },
  });
}

export function convertLead(
  scope: EventsScope,
  leadId: number,
  values: ConvertLeadInput,
): Promise<EventsBooking> {
  return scopedJson(scope, `/leads/${leadId}/convert/`, 'Create booking', {
    method: 'POST',
    body: values,
  });
}

export function fetchPipeline(scope: EventsScope): Promise<EventsPipeline> {
  return scopedJson(scope, '/pipeline/', 'Pipeline');
}

/**
 * At venue level the backend requires the path venue to equal X-Store-ID,
 * so callers pass the venue that owns the record they are working on.
 */
export function fetchVenueSpaces(
  scope: EventsScope,
  venueId: number,
): Promise<EventsSpace[]> {
  return scopedJson(scope, `/venues/${venueId}/spaces/`, 'Spaces');
}

export function fetchBooking(
  scope: EventsScope,
  bookingId: number,
): Promise<EventsBooking> {
  return scopedJson(scope, `/bookings/${bookingId}/`, 'Booking');
}

export function fetchBookingTimeline(
  scope: EventsScope,
  bookingId: number,
): Promise<EventsTimelineEntry[]> {
  return scopedJson(scope, `/bookings/${bookingId}/timeline/`, 'Timeline');
}

/*
 * Proposal, catalog and pricing calls run at the booking's venue: quotes and
 * line edits price against that venue's catalog and rules, and the backend
 * refuses them at organization level.
 */

export function fetchBookingProposal(
  scope: EventsScope,
  bookingId: number,
): Promise<BookingProposalSummary> {
  return scopedJson(scope, `/bookings/${bookingId}/proposal/`, 'Proposal');
}

export function fetchCatalogItems(
  scope: EventsScope,
): Promise<EventsCatalogItem[]> {
  return scopedJson(scope, '/catalog/items/', 'Catalog');
}

export function quoteLines(
  scope: EventsScope,
  lines: ProposalLineInput[],
): Promise<PricingResult> {
  return scopedJson(scope, '/pricing/quote/', 'Quote', {
    method: 'POST',
    body: { lines },
  });
}

export function createProposalDraft(
  scope: EventsScope,
  bookingId: number,
  values: ProposalDraftInput,
): Promise<ProposalVersion> {
  return scopedJson(
    scope,
    `/bookings/${bookingId}/proposals/`,
    'Create proposal',
    {
      method: 'POST',
      body: values,
    },
  );
}

export function updateProposalVersion(
  scope: EventsScope,
  versionId: number,
  values: ProposalDraftInput,
): Promise<ProposalVersion> {
  return scopedJson(
    scope,
    `/proposal-versions/${versionId}/`,
    'Save proposal',
    {
      method: 'PATCH',
      body: values,
    },
  );
}

export function previewProposalVersion(
  scope: EventsScope,
  versionId: number,
): Promise<ProposalPreview> {
  return scopedJson(
    scope,
    `/proposal-versions/${versionId}/preview/`,
    'Preview proposal',
    { method: 'POST', body: {} },
  );
}

export function publishProposalVersion(
  scope: EventsScope,
  versionId: number,
): Promise<PublishResult> {
  return scopedJson(
    scope,
    `/proposal-versions/${versionId}/request-publish/`,
    'Publish proposal',
    { method: 'POST', body: {} },
  );
}

export function reviseProposalVersion(
  scope: EventsScope,
  versionId: number,
): Promise<ProposalVersion> {
  return scopedJson(
    scope,
    `/proposal-versions/${versionId}/revise/`,
    'Revise proposal',
    { method: 'POST', body: {} },
  );
}

export function fetchApprovals(
  scope: EventsScope,
  state?: ApprovalState,
): Promise<EventsApproval[]> {
  const query = state ? `?state=${encodeURIComponent(state)}` : '';
  return scopedJson(scope, `/approvals/${query}`, 'Approvals');
}

export function resolveApproval(
  scope: EventsScope,
  approvalId: number,
  decision: 'approve' | 'reject',
  reason: string,
): Promise<EventsApproval> {
  return scopedJson(
    scope,
    `/approvals/${approvalId}/${decision}/`,
    decision === 'approve' ? 'Approve' : 'Reject',
    { method: 'POST', body: { reason } },
  );
}

/**
 * Returns the booking. A retry whose spaces are still taken is not an
 * error: the event stays CONFIRMATION_BLOCKED and the attempt is audited.
 */
export function resolveBlockedConfirmation(
  scope: EventsScope,
  eventId: number,
  values: ResolveBlockedInput,
): Promise<EventsBooking> {
  return scopedJson(
    scope,
    `/events/${eventId}/resolve-blocked/`,
    'Resolve confirmation',
    { method: 'POST', body: values },
  );
}

export function retryBookingRefund(
  scope: EventsScope,
  refundId: number,
): Promise<EventsBookingRefund> {
  return scopedJson(scope, `/refunds/${refundId}/retry/`, 'Retry refund', {
    method: 'POST',
    body: {},
  });
}

/* Settings are venue-level: the path venue must equal X-Store-ID. */

export function fetchVenueSettings(
  scope: EventsScope,
  venueId: number,
): Promise<EventsVenueSettings> {
  return scopedJson(
    scope,
    `/venues/${venueId}/event-settings/`,
    'Venue settings',
  );
}

export function updateVenueSettings(
  scope: EventsScope,
  venueId: number,
  values: VenueSettingsInput,
): Promise<EventsVenueSettings> {
  return scopedJson(
    scope,
    `/venues/${venueId}/event-settings/`,
    'Save venue settings',
    { method: 'PATCH', body: values },
  );
}

export function createSpace(
  scope: EventsScope,
  venueId: number,
  values: SpaceInput,
): Promise<EventsSpace> {
  return scopedJson(scope, `/venues/${venueId}/spaces/`, 'Add space', {
    method: 'POST',
    body: values,
  });
}

export function updateSpace(
  scope: EventsScope,
  spaceId: number,
  values: SpaceInput,
): Promise<EventsSpace> {
  return scopedJson(scope, `/spaces/${spaceId}/`, 'Save space', {
    method: 'PATCH',
    body: values,
  });
}

export function createCatalogItem(
  scope: EventsScope,
  values: CatalogItemInput,
): Promise<EventsCatalogItem> {
  return scopedJson(scope, '/catalog/items/', 'Add item', {
    method: 'POST',
    body: values,
  });
}

export function updateCatalogItem(
  scope: EventsScope,
  itemId: number,
  values: CatalogItemInput,
): Promise<EventsCatalogItem> {
  return scopedJson(scope, `/catalog/items/${itemId}/`, 'Save item', {
    method: 'PATCH',
    body: values,
  });
}

export function fetchCommercialRules(
  scope: EventsScope,
): Promise<CommercialRules> {
  return scopedJson(scope, '/catalog/rules/', 'Pricing rules');
}

export function updateCommercialRules(
  scope: EventsScope,
  values: CommercialRules,
): Promise<CommercialRules> {
  return scopedJson(scope, '/catalog/rules/', 'Save pricing rules', {
    method: 'PUT',
    body: values,
  });
}
